// ──────────────────────────────────────────────────────────────────────────
// Helpers compartidos por los pasos de solo-lectura del wizard (3 Declaraciones,
// 4 Feedback y, más adelante, 5 Resumen): arman los labels legibles a partir
// de los ids guardados en `formValues` (pasos 1 y 2) más los catálogos mock.
// ──────────────────────────────────────────────────────────────────────────

// La vigencia se captura como fecha sola ("yyyy-mm-dd"), pero las columnas
// TP_Convocatoria.FechaHoraInicio/FechaHoraFin siguen siendo datetime y las filas
// antiguas traen hora. Se soportan ambos: la parte de fecha va antes de la "T".
export const formatFecha = (fechaIso) => {
    if (!fechaIso) return null;
    const [anio, mes, dia] = fechaIso.split("T")[0].split("-");
    return `${dia}/${mes}/${anio}`;
}

// La vigencia se captura solo como fecha, pero las columnas siguen siendo
// datetime: se completa la hora aqui, no en el backend.
//
// Se arma la cadena a mano en vez de usar new Date(x).toISOString() a proposito:
// toISOString convierte a UTC, y el deserializer de Java pasa el sufijo 'Z' por
// ZoneId.systemDefault() (ConfigModule.javaTimeModuleTolerante), asi que la fecha
// podia correrse un dia segun la zona horaria del Lambda. Sin 'Z', Java entra por
// LocalDateTime.parse() y guarda exactamente lo que eligio el usuario.
const conHora = (fecha, hora) => (fecha ? `${fecha.slice(0, 10)}T${hora}` : null);

// Inicio de vigencia -> 00:00:00 del dia elegido.
export const aInicioDelDia = (fecha) => conHora(fecha, "00:00:00");

// Fin de vigencia -> 23:59:59 del dia elegido, para que la convocatoria siga
// vigente durante todo su ultimo dia (si se guardara 00:00:00, publicar ese
// mismo dia fallaria con "fecha de fin pasada"). Se usa solo como fallback
// cuando FechaHoraFin todavia no trae hora propia (ver formatFechaHoraLocal).
export const aFinDelDia = (fecha) => conHora(fecha, "23:59:59");

// Fecha de Término (30/09/2026, a pedido de Jody): ahora se elige con hora
// propia via DatePicker (mismo control que Entrevista Detalle), en vez de
// forzarse siempre a las 23:59:59. Arma "YYYY-MM-DDTHH:mm:ss" a partir del
// Date que entrega react-datepicker usando los getters en hora LOCAL
// (getFullYear/getMonth/... en vez de toISOString(), que convierte a UTC) —
// mismo motivo que conHora() de arriba: el deserializer de Java interpreta un
// sufijo 'Z' y desplaza la hora al huso horario del servidor.
export const formatFechaHoraLocal = (fecha) => {
    if (!fecha) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())}`
        + `T${pad(fecha.getHours())}:${pad(fecha.getMinutes())}:${pad(fecha.getSeconds())}`;
};

// Inverso de formatFechaHoraLocal: arma un Date a partir del string guardado en
// formValues.FechaHoraFin ("YYYY-MM-DD" o "YYYY-MM-DDTHH:mm:ss"), para pintar
// el `selected` del DatePicker. Se parsean los componentes a mano y se arma el
// Date con el constructor local (new Date(anio, mes-1, dia, h, m, s)) — NO con
// new Date(fechaIso) directo: ese constructor interpreta un string sin hora
// ("YYYY-MM-DD") como medianoche UTC, y en huso horario de Perú (UTC-5) el
// DatePicker mostraría un día antes del que realmente se guardó.
export const parseFechaHoraLocal = (fechaIso) => {
    if (!fechaIso) return null;
    const [fechaParte, horaParte] = fechaIso.split("T");
    const [anio, mes, dia] = (fechaParte ?? "").split("-").map(Number);
    if (!anio || !mes || !dia) return null;
    const [h, m, s] = (horaParte ?? "").split(":").map(Number);
    return new Date(anio, mes - 1, dia, h || 0, m || 0, s || 0);
};

// Convierte "dd/MM/yyyy" (formato que devuelve GetDetalleConvocatoria) al formato
// "yyyy-MM-dd" que espera el <input type="date"> nativo (InputDate) — es el inverso
// de formatFecha() de arriba. Se usa al precargar el wizard en modo edición
// (NuevaConvocatoriaMasivo.jsx, prop `detalleConvocatoria`).
export const parseFechaDetalleToIso = (fechaDdMmYyyy) => {
    if (!fechaDdMmYyyy) return null;
    const [dia, mes, anio] = fechaDdMmYyyy.split("/");
    if (!dia || !mes || !anio) return null;
    return `${anio}-${mes}-${dia}`;
}

// GetDetalleConvocatoria devuelve TipoProceso como palabra completa ("Traslado"/
// "Promocion", pensada para mostrarse en UI) — el wizard en cambio guarda el
// código ("T"/"P") en formValues.TipoProceso, que es lo que espera InputSelect/
// TIPO_PROCESO_OPTIONS y el payload que se manda a Crear/ActualizarConvocatoria.
export const mapTipoProcesoTextoToCodigo = (texto) => {
    if (!texto) return null;
    return texto.toLowerCase().startsWith("traslado") ? "T" : "P";
}

export const getRecapLabels = (formValues, { tipoProcesoOptions, cargoOptions, regionOptions, sedeOptions }) => {
    const tipoLabel = tipoProcesoOptions.find(o => o.value === formValues.TipoProceso)?.label;
    const cargoLabel = cargoOptions.find(o => o.value === formValues.IdCargo)?.label;
    const regionesLabel = regionOptions.filter(o => formValues.IdsRegiones?.includes(o.value)).map(o => o.label).join(", ") || null;
    const sedesLabel = sedeOptions.filter(o => formValues.IdsSedes?.includes(o.value)).map(o => o.label).join(", ") || null;
    const fechaInicioLabel = formatFecha(formValues.FechaHoraInicio);
    const fechaFinLabel = formatFecha(formValues.FechaHoraFin);
    const vigenciaLabel = (fechaInicioLabel || fechaFinLabel) ? `${fechaInicioLabel ?? "?"} - ${fechaFinLabel ?? "?"}` : null;

    return {
        convocatoriaLabel: formValues.NombreConvocatoria,
        tipoLabel,
        cargoLabel,
        vigenciaLabel,
        regionesLabel,
        sedesLabel,
    }
}
